import React, { useState, useEffect } from 'react';
import { Play, ChevronRight, Tv, Film, Clock } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { getImageUrl } from '../services/tmdb';

const CurrentlyWatching = ({ user, onSelectContent }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [watchType, setWatchType] = useState('personal'); // 'personal' or 'featured'

  useEffect(() => {
    fetchCurrentlyWatching();
  }, [user]);

  const fetchCurrentlyWatching = async () => {
    setLoading(true);
    try {
      const userId = user?.id || '';
      const response = await axios.get(`${API_URL}/api/featured/currently-watching?user_id=${userId}&limit=5`);
      setItems(response.data.items || []);
      setWatchType(response.data.type || 'featured');
    } catch (error) {
      console.error('Failed to fetch currently watching:', error);
      setItems([]);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="px-8 md:px-16 lg:px-24 py-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-5 w-5 text-red-500 animate-pulse" />
          <h2 className="text-xl font-semibold text-white">Loading...</h2>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return null; // Don't show section if no items
  }

  return (
    <div className="px-8 md:px-16 lg:px-24 py-6 bg-gradient-to-b from-black/50 to-transparent">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Play className="h-5 w-5 text-red-500" fill="currentColor" />
          <h2 className="text-xl font-semibold text-white">
            {watchType === 'personal' ? 'Continue Watching' : 'Trending Now on Casitaplex'}
          </h2>
        </div>
        {watchType === 'personal' && (
          <a href="/dashboard" className="text-gray-400 hover:text-white text-sm flex items-center gap-1">
            View All <ChevronRight className="h-4 w-4" />
          </a>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {items.map((item) => (
          <div
            key={item.id}
            onClick={() => onSelectContent({
              id: item.tmdb_id,
              title: item.title,
              name: item.title,
              poster_path: item.poster_path,
              backdrop_path: item.backdrop_path,
              media_type: item.media_type
            })}
            className="relative group cursor-pointer rounded-lg overflow-hidden bg-zinc-800 hover:scale-105 transition-transform duration-200"
          >
            {/* Poster */}
            <div className="aspect-video relative">
              <img
                src={item.backdrop_path ? getImageUrl(item.backdrop_path, 'w500') : getImageUrl(item.poster_path)}
                alt={item.title}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = '/placeholder-movie.svg'; }}
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
              
              {/* Play Button */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                  <Play className="h-6 w-6 text-black ml-1" fill="currentColor" />
                </div>
              </div>

              {/* Media Type Badge */}
              <div className="absolute top-2 left-2">
                {item.media_type === 'tv' ? (
                  <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                    <Tv className="h-3 w-3" /> Series
                  </span>
                ) : (
                  <span className="bg-red-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                    <Film className="h-3 w-3" /> Movie
                  </span>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="p-3">
              <h3 className="text-white font-medium text-sm line-clamp-1">{item.title}</h3>
              
              {/* Progress Info */}
              {item.media_type === 'tv' && item.current_season && (
                <p className="text-gray-400 text-xs mt-1">
                  S{item.current_season} E{item.current_episode}
                </p>
              )}
              
              {/* Progress Bar */}
              {item.progress_percent > 0 && (
                <div className="mt-2">
                  <div className="h-1 bg-zinc-600 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-red-600 rounded-full"
                      style={{ width: `${item.progress_percent}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CurrentlyWatching;
